// SwipeHub Styled Components

import styled, { keyframes } from 'styled-components';
import { theme } from '../theme';

const slideDown = keyframes`
  from {
    transform: translateY(-100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

export const SwipeHubContainer = styled.div`
  /* 100dvh, not 100vh.
   *
   * On mobile browsers 100vh is the LARGE viewport — the height the page would
   * have if the address bar were hidden. While the bar is showing, which is
   * most of the time, 100vh is taller than what you can actually see, so the
   * bottom of the deck (the action buttons) sits below the fold. dvh tracks
   * the visible height as the bar shows and hides. vh stays first as the
   * fallback for browsers without dvh. */
  min-height: 100vh;
  height: 100vh;
  min-height: 100dvh;
  height: 100dvh;
  width: 100%;
  background: #000;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;

  @media (max-width: ${theme.breakpoints.mobile}) {
    /* Clears the iOS home indicator so the bottom row is tappable. */
    padding-bottom: env(safe-area-inset-bottom, 0px);
  }
`;

export const SwipeHubContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  animation: ${fadeIn} 0.5s ease-out;
  /* Add top padding to account for fixed Keep pile header */
  padding-top: 44px;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding-top: 40px;
  }
`;

export const NotificationBanner = styled.div<{ $type: 'match' | 'request' | 'info' | 'warning' }>`
  position: fixed;
  top: 44px; /* Below compact keep pile header */
  left: 50%;
  transform: translateX(-50%);
  /* Above the Keep Pile Review overlay (10010) and the profile peek drawer
     (10021) so feedback fired from those surfaces is still visible. */
  z-index: 10030;
  
  background: ${props => {
    switch (props.$type) {
      case 'match':
        return 'linear-gradient(135deg, #4ECDC4 0%, #44A08D 100%)';
      case 'request':
        return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
      case 'warning':
        return 'linear-gradient(135deg, #FF6B6B 0%, #FF4444 100%)';
      default:
        return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
    }
  }};
  
  color: white;
  padding: ${theme.spacing.md} ${theme.spacing.xl};
  border-radius: ${theme.borderRadius.full};
  box-shadow: ${theme.shadows.heavy};
  
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  
  max-width: 90%;
  width: auto;
  min-width: 300px;
  
  animation: ${slideDown} 0.4s ease-out;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    top: 40px; /* Below compact keep pile header */
    min-width: auto;
    max-width: calc(100% - ${theme.spacing.lg} * 2);
    padding: ${theme.spacing.sm} ${theme.spacing.md};
    font-size: 0.9rem;
  }
`;

export const NotificationIcon = styled.span`
  font-size: 1.5rem;
  line-height: 1;
  flex-shrink: 0;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 1.25rem;
  }
`;

export const NotificationText = styled.span`
  flex: 1;
  font-weight: 600;
  font-size: 1rem;
  line-height: 1.4;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 0.875rem;
  }
`;

export const NotificationButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  width: 28px;
  height: 28px;
  border-radius: ${theme.borderRadius.full};
  font-size: 1.25rem;
  line-height: 1;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.1);
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 24px;
    height: 24px;
    font-size: 1.1rem;
  }
`;

// Loading State
export const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  animation: ${fadeIn} 0.3s ease-out;
`;

export const LoadingSpinner = styled.div`
  width: 60px;
  height: 60px;
  border: 4px solid ${theme.colors.background.secondary};
  border-top-color: ${theme.colors.primary};
  border-radius: ${theme.borderRadius.full};
  animation: spin 1s linear infinite;
  margin-bottom: ${theme.spacing.lg};
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

export const LoadingText = styled.p`
  font-size: 1.125rem;
  color: ${theme.colors.text.secondary};
  margin: 0;
  font-weight: 500;
`;

// Empty State
export const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing.xl};
  text-align: center;
  animation: ${fadeIn} 0.4s ease-out;
`;

export const EmptyStateIcon = styled.div`
  font-size: 5rem;
  margin-bottom: ${theme.spacing.lg};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 4rem;
  }
`;

export const EmptyStateTitle = styled.h2`
  font-size: 1.75rem;
  font-weight: 700;
  color: ${theme.colors.text.primary};
  margin: 0 0 ${theme.spacing.md} 0;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 1.5rem;
  }
`;

export const EmptyStateMessage = styled.p`
  font-size: 1rem;
  color: ${theme.colors.text.secondary};
  margin: 0 0 ${theme.spacing.xl} 0;
  max-width: 400px;
  line-height: 1.6;
`;

export const EmptyStateButton = styled.button`
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.secondary} 100%);
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.md} ${theme.spacing.xl};
  font-size: 1.125rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: ${theme.shadows.medium};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.heavy};
  }
  
  &:active {
    transform: translateY(0);
  }
`;

